/**
 * WorkoutSessionView — Shared component for rendering a member self-workout session.
 * Supports section-level accordions, exercise checkboxes, set details, timers,
 * "Add/Edit/Remove Exercise" actions, and "Mark All Complete / Incomplete".
 *
 * Props:
 *   session            — workout_schedule document
 *   allowEdit          — bool: show Add/Edit/Remove controls
 *   allowComplete      — bool: show checkboxes for completing exercises
 *   enableTimer        — bool: show set timer
 *   isStaff            — bool: staff mode (extra options for MemberSelfWorkoutDialog)
 *   isMemberWorkout    — bool: true when card is member_workout type
 *   onSessionUpdate    — async fn(updatedWorkoutSession) → save to backend
 *   onMarkComplete     — async fn() → mark entire session complete
 *   onMarkUncomplete   — async fn() → reset session completion
 */
import React, { useState, useRef, useEffect, useMemo } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  CheckCircle2,
  Circle,
  Plus,
  Pencil,
  Trash2,
  Timer,
  Copy,
  ChevronDown,
  AlertCircle,
  Dumbbell,
  Play,
  Square,
  Loader2,
  CalendarIcon,
  Trophy,
  Weight,
  Route,
  Clock,
  TrendingUp,
} from "lucide-react";
import { cn } from "../../lib/utils";
import MemberSelfWorkoutDialog from "./MemberSelfWorkoutDialog";
import { v4 as uuidv4 } from "uuid";

// ── Helpers ──────────────────────────────────────────────────────────────────

function metricLabel(workout) {
  const { workoutMetric, setDetails = [], sets = 0 } = workout;
  const count = setDetails.length || sets;
  if (!count) return "";
  if (workoutMetric === "reps") {
    const parts = setDetails.map(
      (s) => `${s.reps ?? 10}${s.weightKg ? `@${s.weightKg}kg` : ""}`,
    );
    return `${count}×${parts.length ? parts[0] : "?"}`;
  }
  if (workoutMetric === "duration")
    return `${count}×${setDetails[0]?.durationMin ?? 1}min`;
  if (workoutMetric === "distance")
    return `${count}×${setDetails[0]?.distanceKm ?? 1}km`;
  if (workoutMetric === "count")
    return `${count}×${setDetails[0]?.count ?? 10}`;
  return `${count} sets`;
}

// ── Timer Component ───────────────────────────────────────────────────────────

function SetTimer({ restSeconds = 30 }) {
  const [running, setRunning] = useState(false);
  const [remaining, setRemaining] = useState(restSeconds);
  const intervalRef = useRef(null);

  const start = () => {
    if (running) return;
    setRunning(true);
    setRemaining(restSeconds);
    intervalRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(intervalRef.current);
          setRunning(false);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
  };

  const stop = () => {
    clearInterval(intervalRef.current);
    setRunning(false);
    setRemaining(restSeconds);
  };

  if (!restSeconds || restSeconds <= 0) return null;

  return (
    <button
      onClick={running ? stop : start}
      className={cn(
        "text-[9px] font-mono px-1.5 py-0.5 rounded border transition-colors flex items-center gap-1 shrink-0",
        running
          ? "border-amber-500/30 text-amber-500 bg-amber-500/10"
          : "border-border/40 text-muted-foreground hover:text-foreground",
      )}
    >
      <Timer className="h-3 w-3" />
      {running ? `${remaining}s` : `${restSeconds}s rest`}
    </button>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function WorkoutSessionView({
  session,
  allowEdit = false,
  allowComplete = false,
  enableTimer = false,
  isStaff = false,
  isMemberWorkout = false,
  onSessionUpdate,
  onMarkComplete,
  onMarkUncomplete,
  onDeleteSession,
}) {
  const workoutSession = session?.workoutSession || { sections: [], notes: "" };
  const sections = workoutSession.sections || [];

  // Dialog state
  const [dialog, setDialog] = useState({
    open: false,
    sectionIdx: null,
    workoutIdx: null,
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    si: null,
    wi: null,
    title: "",
  });
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, type, action, description, workoutTitle, setNumber, sectionName, onConfirm }
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  const [sessionDeleteConfirm, setSessionDeleteConfirm] = useState(false);
  const [sectionDeleteConfirm, setSectionDeleteConfirm] = useState({
    open: false,
    si: null,
    name: "",
  });

  // Completion state — per exercise (key: sectionIdx-workoutIdx)
  const [completed, setCompleted] = useState(() => {
    const map = {};
    sections.forEach((sec, si) => {
      (sec.workouts || []).forEach((w, wi) => {
        map[`${si}-${wi}`] = w._completed || false;
      });
    });
    return map;
  });

  // Completion state — per set (key: sectionIdx-workoutIdx-setIdx)
  const [completedSets, setCompletedSets] = useState(() => {
    const map = {};
    sections.forEach((sec, si) => {
      (sec.workouts || []).forEach((w, wi) => {
        (w.setDetails || []).forEach((set, sIdx) => {
          map[`${si}-${wi}-${sIdx}`] = set.completed || false;
        });
      });
    });
    return map;
  });

  const [expandedSections, setExpandedSections] = useState(() => {
    const map = {};
    if (sections.length > 0) {
      map[`sec-0`] = true;
    }
    return map;
  });
  const [expandedWorkoutId, setExpandedWorkoutId] = useState(null);

  const toggleWorkoutExpand = (key) => {
    const wasExpanded = expandedWorkoutId === key;
    if (!wasExpanded) {
      setTimeout(() => {
        const el = document.getElementById(`workout-container-${key}`);
        el?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 150);
    }
    setExpandedWorkoutId(wasExpanded ? null : key);
  };

  // Timer state for stopwatch
  const [activeTimer, setActiveTimer] = useState(null); // { secIdx, wIdx, setIdx }
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!activeTimer) {
      setElapsed(0);
      return;
    }
    const interval = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [activeTimer]);

  // Sync completion states with updated props
  useEffect(() => {
    const compMap = {};
    const setCompMap = {};
    sections.forEach((sec, si) => {
      (sec.workouts || []).forEach((w, wi) => {
        compMap[`${si}-${wi}`] = w._completed || false;
        (w.setDetails || []).forEach((set, sIdx) => {
          setCompMap[`${si}-${wi}-${sIdx}`] = set.completed || false;
        });
      });
    });
    setCompleted(compMap);
    setCompletedSets(setCompMap);
  }, [sections]);

  const completionMetrics = useMemo(() => {
    if (!sections) return null;

    let totalWeightVolume = 0;
    let maxWeight = 0;
    let totalDistanceKm = 0;
    let totalDurationMin = 0;
    let completedSetsCount = 0;
    let earliestTime = null;
    let latestTime = null;

    sections.forEach((sec, si) => {
      (sec.workouts || []).forEach((w, wi) => {
        const metric = w.workoutMetric || "reps";
        (w.setDetails || []).forEach((set, sIdx) => {
          const isSetChecked = completedSets[`${si}-${wi}-${sIdx}`];
          if (isSetChecked) {
            completedSetsCount++;

            if (set.completedAt) {
              const t = new Date(set.completedAt).getTime();
              if (earliestTime === null || t < earliestTime) earliestTime = t;
              if (latestTime === null || t > latestTime) latestTime = t;
            }

            if (metric === "reps" || !metric) {
              const reps = parseFloat(set.reps) || 0;
              const weight = parseFloat(set.weightKg) || 0;
              totalWeightVolume += reps * weight;
              if (weight > maxWeight) {
                maxWeight = weight;
              }
            } else if (metric === "distance") {
              const dist = parseFloat(set.distanceKm) || 0;
              totalDistanceKm += dist;
            } else if (metric === "duration") {
              const dur = parseFloat(set.durationMin) || 0;
              totalDurationMin += dur;
            }
          }
        });
      });
    });

    let elapsedSeconds = 0;
    if (earliestTime && latestTime && latestTime > earliestTime) {
      elapsedSeconds = Math.round((latestTime - earliestTime) / 1000);
    }

    return {
      totalWeightVolume,
      maxWeight,
      totalDistanceKm,
      totalDurationMin,
      completedSetsCount,
      elapsedSeconds,
    };
  }, [sections, completedSets]);

  if (!session) return null;

  // ── Internal helpers ────────────────────────────────────────────────────────

  const buildUpdatedSession = (newSections) => ({
    ...workoutSession,
    sections: newSections,
  });

  const formatTime = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatMeaningfulTime = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    if (mins === 0) {
      return `${secs} sec`;
    }
    if (secs === 0) {
      return `${mins} mins`;
    }
    return `${mins} mins ${secs} sec`;
  };

  const handleStartTimer = (si, wi, sIdx) => {
    setActiveTimer({ secIdx: si, wIdx: wi, setIdx: sIdx });
    setElapsed(0);
  };

  const handleStopTimerAndComplete = (si, wi, sIdx) => {
    const workout = sections[si].workouts[wi];
    const workoutTitle = workout.title || `Exercise ${wi + 1}`;
    const formatted = formatMeaningfulTime(elapsed);

    setConfirmDialog({
      title: "Set Completed! 🎉",
      type: "timer",
      workoutTitle,
      formattedTime: formatted,
      onConfirm: () => {
        setActiveTimer(null);
        setElapsed(0);
        return toggleSetComplete(si, wi, sIdx, true, true);
      },
    });
  };

  const toggleComplete = (si, wi) => {
    const key = `${si}-${wi}`;
    const nextVal = !completed[key];
    const workout = sections[si].workouts[wi];
    const workoutTitle = workout.title || `Exercise ${wi + 1}`;

    setConfirmDialog({
      title: nextVal ? "Complete Exercise" : "Reset Exercise",
      type: "workout",
      action: nextVal ? "complete" : "reset",
      workoutTitle,
      onConfirm: () => {
        const updated = { ...completed, [key]: nextVal };
        setCompleted(updated);

        const updatedSets = { ...completedSets };
        (workout.setDetails || []).forEach((_, sIdx) => {
          updatedSets[`${si}-${wi}-${sIdx}`] = nextVal;
        });
        setCompletedSets(updatedSets);

        if (onSessionUpdate) {
          const newSections = sections.map((sec, sIdx) => {
            if (sIdx !== si) return sec;
            return {
              ...sec,
              workouts: (sec.workouts || []).map((w, wIdx) => {
                if (wIdx !== wi) return w;
                return {
                  ...w,
                  _completed: nextVal,
                  setDetails: (w.setDetails || []).map((set) => ({
                    ...set,
                    completed: nextVal,
                    completedAt: nextVal ? (set.completedAt || new Date().toISOString()) : null,
                  })),
                };
              }),
            };
          });
          return onSessionUpdate(buildUpdatedSession(newSections));
        }
      },
    });
  };

  const toggleSectionComplete = (si, checked) => {
    const sec = sections[si];
    const sectionName = sec.name || `Section ${si + 1}`;

    setConfirmDialog({
      title: checked ? "Complete Section" : "Reset Section",
      type: "section",
      action: checked ? "complete" : "reset",
      sectionName,
      onConfirm: () => {
        const updated = { ...completed };
        const updatedSets = { ...completedSets };
        (sec.workouts || []).forEach((w, wi) => {
          updated[`${si}-${wi}`] = checked;
          (w.setDetails || []).forEach((_, sIdx) => {
            updatedSets[`${si}-${wi}-${sIdx}`] = checked;
          });
        });
        setCompleted(updated);
        setCompletedSets(updatedSets);

        if (onSessionUpdate) {
          const newSections = sections.map((s, sIdx) => {
            if (sIdx !== si) return s;
            return {
              ...s,
              workouts: (s.workouts || []).map((w, wIdx) => ({
                ...w,
                _completed: checked,
                setDetails: (w.setDetails || []).map((set) => ({
                  ...set,
                  completed: checked,
                  completedAt: checked ? (set.completedAt || new Date().toISOString()) : null,
                })),
              })),
            };
          });
          return onSessionUpdate(buildUpdatedSession(newSections));
        }
      },
    });
  };

  const markAll = (done) => {
    setConfirmDialog({
      title: done ? "Complete Entire Session" : "Reset Entire Session",
      type: "session-bulk",
      action: done ? "complete" : "reset",
      description: done
        ? "Are you sure you want to mark this entire workout session as Completed? This will automatically mark all sections, exercises, and sets as completed."
        : "Are you sure you want to reset this entire workout session? This will mark all sections, exercises, and sets as incomplete.",
      onConfirm: () => {
        const updated = {};
        const updatedSets = {};
        sections.forEach((sec, si) => {
          (sec.workouts || []).forEach((w, wi) => {
            updated[`${si}-${wi}`] = done;
            (w.setDetails || []).forEach((_, sIdx) => {
              updatedSets[`${si}-${wi}-${sIdx}`] = done;
            });
          });
        });
        setCompleted(updated);
        setCompletedSets(updatedSets);

        if (onSessionUpdate) {
          const newSections = sections.map((sec, sIdx) => ({
            ...sec,
            workouts: (sec.workouts || []).map((w, wIdx) => ({
              ...w,
              _completed: done,
              setDetails: (w.setDetails || []).map((set) => ({
                ...set,
                completed: done,
                completedAt: done ? (set.completedAt || new Date().toISOString()) : null,
              })),
            })),
          }));
          return onSessionUpdate(buildUpdatedSession(newSections));
        }
      },
    });
  };

  const toggleSetComplete = (si, wi, sIdx, checked, skipConfirm = false) => {
    const setKey = `${si}-${wi}-${sIdx}`;
    const workout = sections[si].workouts[wi];
    const workoutTitle = workout.title || `Exercise ${wi + 1}`;
    const setNumber = sIdx + 1;

    const proceed = () => {
      const updatedSets = { ...completedSets, [setKey]: checked };
      setCompletedSets(updatedSets);

      const allCompleted = (workout.setDetails || []).every((_, idx) =>
        idx === sIdx ? checked : !!updatedSets[`${si}-${wi}-${idx}`],
      );

      const exerciseKey = `${si}-${wi}`;
      const updatedExercises = { ...completed, [exerciseKey]: allCompleted };
      setCompleted(updatedExercises);

      if (onSessionUpdate) {
        const newSections = sections.map((sec, sIdxLoc) => {
          if (sIdxLoc !== si) return sec;
          return {
            ...sec,
            workouts: (sec.workouts || []).map((w, wIdx) => {
              if (wIdx !== wi) return w;
              return {
                ...w,
                _completed: allCompleted,
                setDetails: (w.setDetails || []).map((set, idx) => {
                  if (idx !== sIdx) return set;
                  return { ...set, completed: checked, completedAt: checked ? new Date().toISOString() : null };
                }),
              };
            }),
          };
        });
        return onSessionUpdate(buildUpdatedSession(newSections));
      }
    };

    if (skipConfirm) {
      return proceed();
    } else {
      setConfirmDialog({
        title: checked ? "Complete Set" : "Uncheck Set",
        type: "set",
        action: checked ? "complete" : "reset",
        workoutTitle,
        setNumber,
        onConfirm: proceed,
      });
    }
  };

  const handleUpdateSetMetric = (si, wi, sIdx, field, val) => {
    if (onSessionUpdate) {
      const newSections = sections.map((sec, sIdxLoc) => {
        if (sIdxLoc !== si) return sec;
        return {
          ...sec,
          workouts: (sec.workouts || []).map((w, wIdx) => {
            if (wIdx !== wi) return w;
            return {
              ...w,
              setDetails: (w.setDetails || []).map((set, idx) => {
                if (idx !== sIdx) return set;
                return { ...set, [field]: val };
              }),
            };
          }),
        };
      });
      onSessionUpdate(buildUpdatedSession(newSections));
    }
  };

  // ── Dialog handlers ─────────────────────────────────────────────────────────

  const openAddDialog = (sectionIdx) =>
    setDialog({ open: true, sectionIdx, workoutIdx: null });

  const openEditDialog = (sectionIdx, workoutIdx) =>
    setDialog({ open: true, sectionIdx, workoutIdx });

  const openDuplicateDialog = (sectionIdx, workoutIdx) => {
    // Open add dialog with duplicate data
    setDialog({ open: true, sectionIdx, workoutIdx: `dup-${workoutIdx}` });
  };

  const getInitialData = () => {
    const { sectionIdx, workoutIdx } = dialog;
    if (sectionIdx === null) return null;
    if (workoutIdx === null) return null;
    if (typeof workoutIdx === "string" && workoutIdx.startsWith("dup-")) {
      const realIdx = Number(workoutIdx.replace("dup-", ""));
      return {
        ...(sections[sectionIdx]?.workouts?.[realIdx] || {}),
        id: uuidv4(),
      };
    }
    return sections[sectionIdx]?.workouts?.[workoutIdx] || null;
  };

  const handleSaveExercise = async (exercise) => {
    const { sectionIdx, workoutIdx } = dialog;
    setSaving(true);
    try {
      const newSections = sections.map((sec, si) => {
        if (si !== sectionIdx) return sec;
        const workouts = [...(sec.workouts || [])];
        if (workoutIdx === null) {
          // Add new
          workouts.push(exercise);
        } else if (
          typeof workoutIdx === "string" &&
          workoutIdx.startsWith("dup-")
        ) {
          // Duplicate after original
          const realIdx = Number(workoutIdx.replace("dup-", ""));
          workouts.splice(realIdx + 1, 0, { ...exercise, id: uuidv4() });
        } else {
          // Edit existing
          workouts[workoutIdx] = exercise;
        }
        return { ...sec, workouts };
      });
      await onSessionUpdate?.(buildUpdatedSession(newSections));
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveExercise = async (si, wi) => {
    const newSections = sections.map((sec, sIdx) => {
      if (sIdx !== si) return sec;
      return {
        ...sec,
        workouts: (sec.workouts || []).filter((_, wIdx) => wIdx !== wi),
      };
    });
    await onSessionUpdate?.(buildUpdatedSession(newSections));
  };

  const handleRemoveSection = async (si) => {
    const newSections = sections.filter((_, idx) => idx !== si);
    await onSessionUpdate?.(buildUpdatedSession(newSections));
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const totalExercises = sections.reduce(
    (acc, sec) => acc + (sec.workouts?.length || 0),
    0,
  );
  const completedCount = Object.values(completed).filter(Boolean).length;
  const allDone = totalExercises > 0 && completedCount >= totalExercises;

  return (
    <div className="space-y-3">
      {/* Top Header Row for Session Actions */}
      {onDeleteSession && (
        <div className="sticky top-[-8px] lg:top-[-24px] z-20 bg-card/95 backdrop-blur-md px-1 py-2 mb-2 border-b border-border/40 flex flex-row items-center justify-between gap-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground font-black flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-primary" />
            {session.date
              ? (() => {
                  const parts = session.date.split("-");
                  if (parts.length === 3) {
                    const dateObj = new Date(
                      Number(parts[0]),
                      Number(parts[1]) - 1,
                      Number(parts[2]),
                    );
                    return dateObj.toLocaleDateString("en-IN", {
                      weekday: "short",
                      day: "numeric",
                      month: "short",
                    });
                  }
                  return session.date;
                })()
              : "Workout Session"}
          </div>
          <button
            onClick={() => setSessionDeleteConfirm(true)}
            className="px-2.5 py-1 text-[9px] font-heading uppercase tracking-wide bg-destructive/10 hover:bg-destructive/20 text-destructive rounded border border-destructive/25 transition-colors cursor-pointer shrink-0"
          >
            <Trash2 className="h-3 w-3 inline mr-1" />
            Delete Session
          </button>
        </div>
      )}

      {/* Mark All buttons */}
      {allowComplete && totalExercises > 0 && (
        <div className="flex items-center gap-2 hidden">
          <button
            onClick={() => markAll(true)}
            className={cn(
              "flex-1 py-1.5 text-[10px] font-heading font-black uppercase tracking-wider rounded-lg border transition-colors",
              allDone
                ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-500"
                : "bg-secondary border-border text-muted-foreground hover:text-foreground",
            )}
          >
            <CheckCircle2 className="inline h-3 w-3 mr-1" />
            Mark All Complete
          </button>
          <button
            onClick={() => markAll(false)}
            className="flex-1 py-1.5 text-[10px] font-heading font-black uppercase tracking-wider rounded-lg border border-border bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          >
            <Circle className="inline h-3 w-3 mr-1" />
            Mark All Incomplete
          </button>
        </div>
      )}

      {/* Completion progress */}
      {allowComplete && totalExercises > 0 && (
        <div
          className={cn(
            "sticky z-20 bg-card/95 backdrop-blur-md py-3 px-1 mb-3 border-b border-border/10 flex items-center gap-3 text-xs text-muted-foreground font-mono",
            onDeleteSession
              ? "top-[32px] lg:top-[8px]"
              : "top-[-8px] lg:top-[-24px]",
          )}
        >
          <div className="flex-1 h-1.5 bg-border rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all duration-300"
              style={{ width: `${(completedCount / totalExercises) * 100}%` }}
            />
          </div>
          <span className="shrink-0 font-semibold text-foreground">
            {completedCount}/{totalExercises} exercises
          </span>
        </div>
      )}

              {allDone && completionMetrics && (
                <div className="bg-gradient-to-br from-emerald-500/10 via-teal-500/5 to-transparent border border-emerald-500/25 rounded-2xl p-5 mb-4 shadow-sm relative overflow-hidden backdrop-blur-sm transition-all duration-300 hover:shadow-md hover:border-emerald-500/40">
                  {/* Decorative faint background icons or gradient blob */}
                  <div className="absolute right-0 bottom-0 translate-x-4 translate-y-4 opacity-5 pointer-events-none">
                    <Trophy className="h-40 w-40 text-emerald-500" />
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0 shadow-inner animate-pulse">
                      <Trophy className="h-6 w-6 text-emerald-500 dark:text-emerald-400" />
                    </div>
                    <div className="space-y-1 select-text text-left">
                      <h3 className="text-base font-heading font-extrabold tracking-tight text-foreground flex items-center gap-1.5">
                        Workout Day Completed! 🎉
                      </h3>
                      <p className="text-xs text-muted-foreground font-medium">
                        Phenomenal job! You crushed today's routine. Here is a summary of your achievements:
                      </p>
                    </div>
                  </div>

                  {/* Metrics grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4 pt-2">
                    {completionMetrics.totalWeightVolume > 0 && (
                      <div className="bg-card/40 border border-border/50 rounded-xl p-3 flex flex-col justify-between text-left transition-all hover:bg-card/65">
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Weight className="h-4 w-4 text-emerald-500" />
                          <span className="text-[10px] font-semibold uppercase tracking-wider">Weight Lifted</span>
                        </div>
                        <div className="mt-2">
                          <span className="text-base font-mono font-black text-foreground">
                            {completionMetrics.totalWeightVolume.toLocaleString()}
                          </span>
                          <span className="text-[11px] font-bold text-muted-foreground ml-0.5">kg</span>
                        </div>
                        {completionMetrics.maxWeight > 0 && (
                          <div className="text-[10px] text-muted-foreground/80 mt-1 font-medium font-sans">
                            Max Lift: <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">{completionMetrics.maxWeight} kg</span>
                          </div>
                        )}
                      </div>
                    )}

                    {completionMetrics.totalDistanceKm > 0 && (
                      <div className="bg-card/40 border border-border/50 rounded-xl p-3 flex flex-col justify-between text-left transition-all hover:bg-card/65">
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Route className="h-4 w-4 text-teal-500" />
                          <span className="text-[10px] font-semibold uppercase tracking-wider">Distance</span>
                        </div>
                        <div className="mt-2">
                          <span className="text-base font-mono font-black text-foreground">
                            {completionMetrics.totalDistanceKm.toFixed(2)}
                          </span>
                          <span className="text-[11px] font-bold text-muted-foreground ml-0.5">km</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground/80 mt-1 font-medium font-sans">
                          Cardio track
                        </div>
                      </div>
                    )}

                    {(completionMetrics.totalDurationMin > 0 || completionMetrics.elapsedSeconds > 30) && (
                      <div className="bg-card/40 border border-border/50 rounded-xl p-3 flex flex-col justify-between text-left transition-all hover:bg-card/65">
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Clock className="h-4 w-4 text-amber-500" />
                          <span className="text-[10px] font-semibold uppercase tracking-wider">Time Taken</span>
                        </div>
                        <div className="mt-2">
                          <span className="text-base font-mono font-black text-foreground">
                            {completionMetrics.elapsedSeconds > 30
                              ? (completionMetrics.elapsedSeconds >= 3600
                                ? `${Math.floor(completionMetrics.elapsedSeconds / 3600)}h ${Math.floor((completionMetrics.elapsedSeconds % 3600) / 60)}m`
                                : `${Math.floor(completionMetrics.elapsedSeconds / 60)}m ${completionMetrics.elapsedSeconds % 60}s`)
                              : `${completionMetrics.totalDurationMin} mins`
                            }
                          </span>
                        </div>
                        {completionMetrics.elapsedSeconds > 30 && completionMetrics.totalDurationMin > 0 && (
                          <div className="text-[10px] text-muted-foreground/80 mt-1 font-medium font-sans">
                            Active duration: <span className="font-mono font-bold">{completionMetrics.totalDurationMin} mins</span>
                          </div>
                        )}
                        {!(completionMetrics.elapsedSeconds > 30) && (
                          <div className="text-[10px] text-muted-foreground/80 mt-1 font-medium font-sans">
                            Set duration metric
                          </div>
                        )}
                      </div>
                    )}

                    <div className="bg-card/40 border border-border/50 rounded-xl p-3 flex flex-col justify-between text-left transition-all hover:bg-card/65">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <TrendingUp className="h-4 w-4 text-blue-500" />
                        <span className="text-[10px] font-semibold uppercase tracking-wider">Volume & Sets</span>
                      </div>
                      <div className="mt-2">
                        <span className="text-base font-mono font-black text-foreground">
                          {completionMetrics.completedSetsCount}
                        </span>
                        <span className="text-[11px] font-bold text-muted-foreground ml-0.5">Sets done</span>
                      </div>
                      <div className="text-[10px] text-muted-foreground/80 mt-1 font-medium font-sans">
                        {totalExercises} exercises completed
                      </div>
                    </div>
                  </div>

                  {/* Motivational message dynamic quote block */}
                  <div className="mt-4 pt-3 border-t border-emerald-500/15 flex items-start gap-2 text-left">
                    <span className="text-base select-none mt-0.5">💡</span>
                    <p className="text-[11px] text-muted-foreground leading-relaxed italic font-medium">
                      {(() => {
                        if (completionMetrics.totalWeightVolume > 3000) {
                          return "Incredible power! Lifting over 3,000 kg of volume is top-tier strength work. Keep raising the bar!";
                        } else if (completionMetrics.totalWeightVolume > 1000) {
                          return "Amazing strength volume! Moving over 1,000 kg is a serious milestone. Your muscles are thanking you!";
                        } else if (completionMetrics.totalDistanceKm > 5) {
                          return "Spectacular stamina! Completing more than 5 km is an outstanding cardio achievement. You're building a bulletproof heart!";
                        } else if (completionMetrics.totalDistanceKm > 2) {
                          return "Awesome endurance! Over 2 km covered shows great dedication and consistent pacing.";
                        } else if (completionMetrics.totalDurationMin > 45 || completionMetrics.elapsedSeconds > 2700) {
                          return "Superb dedication! A workout exceeding 45 minutes builds solid stamina, character, and consistency.";
                        } else {
                          return "Consistency is the foundation of greatness. Every set, rep, and minute counts towards your ultimate goal. Rest well and crush the next one!";
                        }
                      })()}
                    </p>
                  </div>
                </div>
              )}

      {/* Sections */}
      {sections.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground text-sm">
          <Dumbbell className="h-8 w-8 mx-auto mb-2 opacity-30" />
          <p>No exercises added yet.</p>
          {allowEdit && (
            <p className="text-xs mt-1">
              Use "Add Exercise" buttons below to get started.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {sections.map((section, si) => {
            const sectionExercises = section.workouts || [];
            const sectionCompleted = sectionExercises.filter(
              (_, wi) => completed[`${si}-${wi}`],
            ).length;
            const sectionDone =
              sectionExercises.length > 0 &&
              sectionCompleted >= sectionExercises.length;

            const sectionKey = `sec-${si}`;
            const isCollapsed = !expandedSections[sectionKey];

            return (
              <div
                key={section.id || si}
                id={`section-container-${sectionKey}`}
                className={cn(
                  "space-y-2 px-2 py-4 rounded-lg border select-none scroll-mt-4 transition-all duration-200",
                  sectionDone
                    ? "bg-emerald-500/5 dark:bg-emerald-500/10 border-emerald-500/20"
                    : "bg-secondary/100 border-border/40",
                )}
              >
                {/* Section Header */}
                <div
                  onClick={() =>
                    setExpandedSections((prev) => {
                      const wasExpanded = !!prev[sectionKey];
                      if (!wasExpanded) {
                        setTimeout(() => {
                          const el = document.getElementById(
                            `section-container-${sectionKey}`,
                          );
                          el?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }, 150);
                        return { [sectionKey]: true };
                      } else {
                        return {};
                      }
                    })
                  }
                  className="flex items-center justify-between cursor-pointer hover:bg-secondary/20 px-1 rounded transition-colors group select-none py-1"
                >
                  <div className="flex items-center gap-2">
                    <ChevronDown
                      className={cn(
                        "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
                        isCollapsed && "-rotate-90",
                      )}
                    />
                    {allowComplete && (
                      <Checkbox
                        checked={sectionDone}
                        onCheckedChange={(checked) => {
                          toggleSectionComplete(si, !!checked);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="h-5 w-5 text-primary border-muted-foreground/45 rounded mr-0.5 bg-card"
                      />
                    )}
                    <span
                      className={cn(
                        "text-[13px] font-heading font-bold uppercase tracking-wider",
                        sectionDone
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-muted-foreground",
                      )}
                    >
                      {section.name || `Section ${si + 1}`}
                    </span>
                  </div>

                  <div
                    className={cn(
                      "text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ml-auto mr-2 shrink-0 flex items-center gap-1",
                      sectionDone
                        ? "bg-emerald-500/10 dark:bg-emerald-500/20"
                        : "bg-secondary",
                    )}
                  >
                    <span
                      className={
                        sectionCompleted > 0
                          ? "text-emerald-500 dark:text-emerald-400"
                          : "text-muted-foreground/60"
                      }
                    >
                      {sectionCompleted} done
                    </span>
                    <span className="text-muted-foreground/30">•</span>
                    <span
                      className={
                        sectionExercises.length - sectionCompleted > 0
                          ? "text-amber-500 dark:text-amber-400"
                          : "text-muted-foreground/60"
                      }
                    >
                      {sectionExercises.length - sectionCompleted} left
                    </span>
                    {allowEdit && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          setSectionDeleteConfirm({
                            open: true,
                            si,
                            name: section.name || `Section ${si + 1}`,
                          });
                        }}
                        className="p-1 text-muted-foreground/45 hover:text-destructive transition-colors rounded hover:bg-secondary/40 cursor-pointer shrink-0 flex items-center justify-center ml-1"
                        title="Delete Section"
                        role="button"
                      >
                        <Trash2 className="h-3 w-3" />
                      </span>
                    )}
                  </div>
                </div>

                {!isCollapsed && (
                  <div className="space-y-2 mt-2">
                    {sectionExercises.length === 0 && (
                      <p className="text-xs text-muted-foreground py-3 text-center">
                        No exercises in this section.
                      </p>
                    )}
                    {sectionExercises.map((workout, wi) => {
                      const key = `${si}-${wi}`;
                      const isDone = completed[key] || false;

                      return (
                        <div
                          key={workout.id || wi}
                          id={`workout-container-${key}`}
                          className={cn(
                            "border rounded-xl scroll-mt-20 lg:scroll-mt-24 transition-all duration-200",
                            isDone
                              ? "bg-emerald-500/5 border-emerald-500/20"
                              : "bg-card border-border/80",
                          )}
                        >
                          {/* Workout Header */}
                          <div
                            onClick={() => toggleWorkoutExpand(key)}
                            className="p-3 flex items-center flex-col justify-between gap-1 cursor-pointer hover:bg-secondary/10 transition-colors select-none"
                          >
                            <div className="flex justify-between w-full">
                              <div className="flex items-center gap-2">
                                {allowComplete && (
                                  <Checkbox
                                    checked={isDone}
                                    onCheckedChange={() =>
                                      toggleComplete(si, wi)
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    className="h-5 w-5 text-primary border-muted-foreground/45 rounded"
                                  />
                                )}
                                <div className="flex items-center gap-2">
                                  <div className="h-5.5 w-5.5 rounded bg-secondary flex items-center justify-center shrink-0 overflow-hidden">
                                    <Dumbbell className="h-2.5 w-2.5 text-muted-foreground" />
                                  </div>
                                  <div className="flex flex-col">
                                    <span
                                      className={cn(
                                        "font-semibold text-[12px]",
                                        isDone
                                          ? "line-through text-muted-foreground"
                                          : "text-foreground",
                                      )}
                                    >
                                      {workout.title || `Exercise ${wi + 1}`}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div
                                className="flex items-center gap-2"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Badge
                                  variant="outline"
                                  className="text-[8px] font-mono uppercase px-1 py-0"
                                >
                                  {workout.workoutMetric || "reps"}
                                </Badge>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleWorkoutExpand(key);
                                  }}
                                  className="p-1 hover:bg-secondary rounded text-muted-foreground transition-colors"
                                >
                                  <ChevronDown
                                    className={cn(
                                      "h-3.5 w-3.5 transition-transform duration-200",
                                      expandedWorkoutId === key && "rotate-180",
                                    )}
                                  />
                                </button>
                              </div>
                            </div>
                            <div className="flex items-center justify-between w-full pl-7">
                              <span className="text-[10px] text-muted-foreground font-mono font-medium mt-0.5">
                                {(() => {
                                  const numSets =
                                    workout.setDetails?.length ||
                                    workout.sets ||
                                    0;
                                  let repsLabel = "";
                                  const setsList = workout.setDetails || [];
                                  if (
                                    workout.workoutMetric === "reps" ||
                                    !workout.workoutMetric
                                  ) {
                                    const repsList = setsList
                                      .map((s) => s.reps)
                                      .filter(
                                        (r) =>
                                          r !== undefined &&
                                          r !== null &&
                                          r !== "",
                                      );
                                    if (repsList.length > 0) {
                                      const minReps = Math.min(...repsList);
                                      const maxReps = Math.max(...repsList);
                                      repsLabel =
                                        minReps === maxReps
                                          ? `${minReps} Reps`
                                          : `${minReps}-${maxReps} Reps`;
                                    }
                                  } else if (
                                    workout.workoutMetric === "duration"
                                  ) {
                                    const durList = setsList
                                      .map((s) => s.durationMin)
                                      .filter(
                                        (d) =>
                                          d !== undefined &&
                                          d !== null &&
                                          d !== "",
                                      );
                                    if (durList.length > 0) {
                                      const minDur = Math.min(...durList);
                                      const maxDur = Math.max(...durList);
                                      repsLabel =
                                        minDur === maxDur
                                          ? `${minDur} mins`
                                          : `${minDur}-${maxDur} mins`;
                                    }
                                  } else if (
                                    workout.workoutMetric === "distance"
                                  ) {
                                    const distList = setsList
                                      .map((s) => s.distanceKm)
                                      .filter(
                                        (d) =>
                                          d !== undefined &&
                                          d !== null &&
                                          d !== "",
                                      );
                                    if (distList.length > 0) {
                                      const minDist = Math.min(...distList);
                                      const maxDist = Math.max(...distList);
                                      repsLabel =
                                        minDist === maxDist
                                          ? `${minDist} km`
                                          : `${minDist}-${maxDist} km`;
                                    }
                                  } else if (
                                    workout.workoutMetric === "count"
                                  ) {
                                    const countList = setsList
                                      .map((s) => s.count)
                                      .filter(
                                        (c) =>
                                          c !== undefined &&
                                          c !== null &&
                                          c !== "",
                                      );
                                    if (countList.length > 0) {
                                      const minCount = Math.min(...countList);
                                      const maxCount = Math.max(...countList);
                                      repsLabel =
                                        minCount === maxCount
                                          ? `${minCount} Counts`
                                          : `${minCount}-${maxCount} Counts`;
                                    }
                                  }
                                  return `${numSets} Set${numSets !== 1 ? "s" : ""}${repsLabel ? ` • ${repsLabel}` : ""}`;
                                })()}
                              </span>
                              {allowEdit && (
                                <div className="flex gap-1 shrink-0">
                                  <button
                                    onClick={() => openDuplicateDialog(si, wi)}
                                    title="Duplicate"
                                    className="p-1 text-muted-foreground/50 hover:text-primary transition-colors"
                                  >
                                    <Copy className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    onClick={() => openEditDialog(si, wi)}
                                    title="Edit"
                                    className="p-1 text-muted-foreground/50 hover:text-primary transition-colors"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      const title =
                                        workout.title || `Exercise ${wi + 1}`;
                                      setDeleteConfirm({
                                        open: true,
                                        si,
                                        wi,
                                        title,
                                      });
                                    }}
                                    title="Remove"
                                    className="p-1 text-muted-foreground/50 hover:text-destructive transition-colors"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Workout Content */}
                          {expandedWorkoutId === key && (
                            <div className="p-2 space-y-2 border-t border-border/40 bg-secondary/10 rounded-b-xl">
                              {workout.instructions && (
                                <div className="text-[10.5px] leading-relaxed border-l-2 border-amber-500 bg-amber-500/5 dark:bg-amber-500/10 p-2.5 rounded-r-lg my-2 font-medium text-foreground/90 flex items-start gap-2 shadow-sm">
                                  <AlertCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: workout.instructions,
                                    }}
                                  />
                                </div>
                              )}

                              {workout.setDetails?.length > 0 && (
                                <div className="space-y-2">
                                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
                                    <span>
                                      Total Sets ({workout.setDetails.length})
                                    </span>
                                    {allowComplete && (
                                      <>
                                        <span className="text-muted-foreground/30 font-normal">
                                          |
                                        </span>
                                        <span className="text-primary/70">
                                          Customize the values
                                        </span>
                                      </>
                                    )}
                                  </p>
                                  <div className="space-y-2">
                                    {workout.setDetails.map((set, setIdx) => {
                                      let suggestText = "";
                                      if (workout.workoutMetric === "reps") {
                                        suggestText = `${set.reps ?? 10} reps ${set.weightKg ? `@${set.weightKg} kg` : ""}`;
                                      } else if (
                                        workout.workoutMetric === "duration"
                                      ) {
                                        suggestText = `${set.durationMin ?? 1} mins`;
                                      } else if (
                                        workout.workoutMetric === "distance"
                                      ) {
                                        suggestText = `${set.distanceKm ?? 1} kms`;
                                      } else if (
                                        workout.workoutMetric === "count"
                                      ) {
                                        suggestText = `${set.count ?? 10} counts`;
                                      }

                                      const isSetChecked =
                                        completedSets[
                                          `${si}-${wi}-${setIdx}`
                                        ] || false;
                                      const isInputDisabled =
                                        !allowComplete || isSetChecked;

                                      return (
                                        <div
                                          key={setIdx}
                                          className={cn(
                                            "flex flex-wrap items-center justify-between gap-1.5 p-1 px-1.5 rounded-lg border text-[11px] select-none transition-all duration-200",
                                            isSetChecked
                                              ? "bg-emerald-500/5 border-emerald-500/20 text-foreground"
                                              : "bg-secondary/100 border-border/40 text-muted-foreground",
                                          )}
                                        >
                                          <div
                                            className="flex items-center gap-1.5 cursor-pointer py-0.5"
                                            onClick={() => {
                                              if (allowComplete) {
                                                toggleSetComplete(
                                                  si,
                                                  wi,
                                                  setIdx,
                                                  !isSetChecked,
                                                );
                                              }
                                            }}
                                          >
                                            {allowComplete && (
                                              <Checkbox
                                                checked={isSetChecked}
                                                onCheckedChange={(checked) => {
                                                  toggleSetComplete(
                                                    si,
                                                    wi,
                                                    setIdx,
                                                    !!checked,
                                                  );
                                                }}
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                                className="h-5 w-5 text-emerald-500 border-muted-foreground/30 rounded-md pointer-events-none bg-card"
                                              />
                                            )}
                                            <span className="font-mono text-[11px] font-bold text-foreground">
                                              Set {setIdx + 1}
                                            </span>
                                            {suggestText && (
                                              <span className="text-[8.5px] text-muted-foreground">
                                                ({suggestText})
                                              </span>
                                            )}

                                            {allowComplete &&
                                              (enableTimer ||
                                                workout.enableTimer) && (
                                                <div
                                                  className="flex items-center gap-1.5 ml-1.5"
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                >
                                                  {activeTimer &&
                                                  activeTimer.secIdx === si &&
                                                  activeTimer.wIdx === wi &&
                                                  activeTimer.setIdx ===
                                                    setIdx ? (
                                                    <div className="flex items-center gap-1.5">
                                                      <span className="text-orange-500 font-mono text-[9.5px] font-bold animate-pulse bg-orange-500/10 px-1 py-0.5 rounded border border-orange-500/20">
                                                        {formatTime(elapsed)}
                                                      </span>
                                                      <button
                                                        onClick={() =>
                                                          handleStopTimerAndComplete(
                                                            si,
                                                            wi,
                                                            setIdx,
                                                          )
                                                        }
                                                        className="px-1.5 py-0.5 rounded bg-red-500 hover:bg-red-600 text-white text-[9px] font-medium flex items-center gap-0.5 transition-colors border-0 shrink-0 cursor-pointer"
                                                      >
                                                        <Square className="h-2 w-2 fill-white text-white" />
                                                        Stop & Complete
                                                      </button>
                                                    </div>
                                                  ) : (
                                                    !isSetChecked && (
                                                      <button
                                                        onClick={() =>
                                                          handleStartTimer(
                                                            si,
                                                            wi,
                                                            setIdx,
                                                          )
                                                        }
                                                        className="px-1.5 py-0.5 rounded bg-primary hover:bg-primary/90 text-primary-foreground text-[9px] font-medium flex items-center gap-0.5 transition-colors border-0 shrink-0 cursor-pointer"
                                                      >
                                                        <Play className="h-2 w-2 fill-current" />
                                                        Start
                                                      </button>
                                                    )
                                                  )}
                                                </div>
                                              )}
                                          </div>

                                          <div className="flex items-center gap-1 text-[10px]">
                                            {workout.workoutMetric ===
                                              "reps" && (
                                              <>
                                                <div className="flex items-center gap-0.5 w-full">
                                                  <input
                                                    type="number"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    disabled={isInputDisabled}
                                                    value={
                                                      set.reps !== undefined
                                                        ? set.reps
                                                        : ""
                                                    }
                                                    onChange={(e) => {
                                                      const val =
                                                        e.target.value;
                                                      handleUpdateSetMetric(
                                                        si,
                                                        wi,
                                                        setIdx,
                                                        "reps",
                                                        val === ""
                                                          ? ""
                                                          : parseInt(val) || 0,
                                                      );
                                                    }}
                                                    className="w-[60px] h-7 text-center bg-card border border-border/80 rounded font-mono p-0.5 text-xs text-foreground disabled:opacity-75"
                                                  />
                                                  <span className="text-[9.5px]">
                                                    r
                                                  </span>
                                                </div>
                                                <span>@</span>
                                                <div className="flex items-center gap-0.5">
                                                  <input
                                                    type="number"
                                                    inputMode="decimal"
                                                    disabled={isInputDisabled}
                                                    value={
                                                      set.weightKg !== undefined
                                                        ? set.weightKg
                                                        : ""
                                                    }
                                                    onChange={(e) => {
                                                      const val =
                                                        e.target.value;
                                                      handleUpdateSetMetric(
                                                        si,
                                                        wi,
                                                        setIdx,
                                                        "weightKg",
                                                        val === ""
                                                          ? ""
                                                          : parseFloat(val) ||
                                                              0,
                                                      );
                                                    }}
                                                    className="w-[60px] h-7 text-center bg-card border border-border/80 rounded font-mono p-0.5 text-xs text-foreground disabled:opacity-75"
                                                  />
                                                  <span className="text-[9.5px]">
                                                    kg
                                                  </span>
                                                </div>
                                              </>
                                            )}

                                            {workout.workoutMetric ===
                                              "duration" && (
                                              <div className="flex items-center gap-0.5">
                                                <input
                                                  type="number"
                                                  inputMode="decimal"
                                                  disabled={isInputDisabled}
                                                  value={
                                                    set.durationMin !==
                                                    undefined
                                                      ? set.durationMin
                                                      : ""
                                                  }
                                                  onChange={(e) => {
                                                    const val = e.target.value;
                                                    handleUpdateSetMetric(
                                                      si,
                                                      wi,
                                                      setIdx,
                                                      "durationMin",
                                                      val === ""
                                                        ? ""
                                                        : parseFloat(val) || 0,
                                                    );
                                                  }}
                                                  className="w-[60px] h-7 text-center bg-card border border-border/80 rounded font-mono p-0.5 text-xs text-foreground disabled:opacity-75"
                                                />
                                                <span className="text-[9.5px]">
                                                  min
                                                </span>
                                              </div>
                                            )}

                                            {workout.workoutMetric ===
                                              "distance" && (
                                              <div className="flex items-center gap-0.5">
                                                <input
                                                  type="number"
                                                  inputMode="decimal"
                                                  disabled={isInputDisabled}
                                                  value={
                                                    set.distanceKm !== undefined
                                                      ? set.distanceKm
                                                      : ""
                                                  }
                                                  onChange={(e) => {
                                                    const val = e.target.value;
                                                    handleUpdateSetMetric(
                                                      si,
                                                      wi,
                                                      setIdx,
                                                      "distanceKm",
                                                      val === ""
                                                        ? ""
                                                        : parseFloat(val) || 0,
                                                    );
                                                  }}
                                                  className="w-[60px] h-7 text-center bg-card border border-border/80 rounded font-mono p-0.5 text-xs text-foreground disabled:opacity-75"
                                                />
                                                <span className="text-[9.5px]">
                                                  km
                                                </span>
                                              </div>
                                            )}

                                            {workout.workoutMetric ===
                                              "count" && (
                                              <div className="flex items-center gap-0.5">
                                                <input
                                                  type="number"
                                                  inputMode="numeric"
                                                  pattern="[0-9]*"
                                                  disabled={isInputDisabled}
                                                  value={
                                                    set.count !== undefined
                                                      ? set.count
                                                      : ""
                                                  }
                                                  onChange={(e) => {
                                                    const val = e.target.value;
                                                    handleUpdateSetMetric(
                                                      si,
                                                      wi,
                                                      setIdx,
                                                      "count",
                                                      val === ""
                                                        ? ""
                                                        : parseInt(val) || 0,
                                                    );
                                                  }}
                                                  className="w-[60px] h-7 text-center bg-card border border-border/80 rounded font-mono p-0.5 text-xs text-foreground disabled:opacity-75"
                                                />
                                                <span className="text-[9.5px]">
                                                  c
                                                </span>
                                              </div>
                                            )}

                                            {(enableTimer ||
                                              workout.enableTimer) &&
                                              set.restSeconds > 0 && (
                                                <SetTimer
                                                  restSeconds={Number(
                                                    set.restSeconds,
                                                  )}
                                                />
                                              )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Add Exercise button per section */}
                    {allowEdit && (
                      <button
                        onClick={() => openAddDialog(si)}
                        className="w-full flex items-center justify-center gap-1.5 py-2 border border-dashed border-primary/30 rounded-lg text-primary text-[11px] font-medium hover:bg-primary/5 transition-colors"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Add Exercise to {section.name || `Section ${si + 1}`}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Notes */}
      {workoutSession.notes?.trim() && (
        <div className="text-[11px] text-muted-foreground italic bg-secondary/20 rounded-lg px-3 py-2 border border-border/30">
          <span className="font-semibold text-foreground not-italic mr-1">
            Notes:
          </span>
          {workoutSession.notes}
        </div>
      )}

      {/* Exercise Dialog */}
      <MemberSelfWorkoutDialog
        open={dialog.open}
        onClose={() =>
          setDialog({ open: false, sectionIdx: null, workoutIdx: null })
        }
        initialData={getInitialData()}
        isStaff={isStaff}
        onSave={handleSaveExercise}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirm.open}
        onOpenChange={(o) => setDeleteConfirm((prev) => ({ ...prev, open: o }))}
      >
        <DialogContent className="bg-card border-border max-w-sm w-[90%] rounded-xl p-5 z-[99]">
          <DialogHeader>
            <DialogTitle className="font-heading text-sm font-black uppercase tracking-wide text-foreground flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-destructive" />
              Delete Exercise
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mt-2 leading-relaxed">
              Are you sure you want to delete{" "}
              <strong>"{deleteConfirm.title}"</strong>? This will remove it from
              this workout session.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 justify-end mt-4 flex-row">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setDeleteConfirm({ open: false, si: null, wi: null, title: "" })
              }
              className="text-xs font-heading uppercase tracking-wide"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={deleting}
              onClick={async () => {
                setDeleting(true);
                try {
                  await handleRemoveExercise(
                    deleteConfirm.si,
                    deleteConfirm.wi,
                  );
                  setDeleteConfirm({
                    open: false,
                    si: null,
                    wi: null,
                    title: "",
                  });
                } finally {
                  setDeleting(false);
                }
              }}
              className="text-xs font-heading uppercase tracking-wide bg-destructive text-destructive-foreground hover:bg-destructive/90 min-w-[70px] flex items-center justify-center gap-1.5"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for checkboxes */}
      <Dialog
        open={!!confirmDialog}
        onOpenChange={(open) => {
          if (!open && !confirmSubmitting) setConfirmDialog(null);
        }}
      >
        <DialogContent className="max-w-sm bg-card border-border p-5 rounded-lg flex flex-col gap-4 z-[99]">
          <DialogHeader className="text-left space-y-1.5 shrink-0">
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold font-heading uppercase tracking-wide">
              {confirmDialog?.title}
            </DialogTitle>
            <div className="text-[11.5px] text-muted-foreground leading-relaxed flex flex-col gap-2">
              {confirmDialog?.type === "timer" && (
                <div className="flex flex-col gap-2">
                  <p>Awesome effort! You finished your set.</p>
                  <div className="my-1 p-2.5 bg-secondary/35 rounded-lg border border-border/40 flex items-center justify-between">
                    <span className="font-semibold text-foreground text-xs">
                      {confirmDialog.workoutTitle}
                    </span>
                    <span className="text-orange-500 font-mono text-[10px] font-bold bg-orange-500/10 px-1.5 py-0.5 rounded border border-orange-500/20">
                      {confirmDialog.formattedTime}
                    </span>
                  </div>
                  <p className="font-medium text-foreground">
                    Would you like to mark this set as completed?
                  </p>
                </div>
              )}
              {confirmDialog?.type === "set" && (
                <div className="flex flex-col gap-2">
                  <p>Are you sure you want to update this set's status?</p>
                  <div className="my-1 p-2.5 bg-secondary/35 rounded-lg border border-border/40 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[9.5px] font-bold bg-primary/15 text-primary px-1.5 py-0.5 rounded border border-primary/25">
                        Set {confirmDialog.setNumber}
                      </span>
                      <span className="font-semibold text-foreground text-xs">
                        {confirmDialog.workoutTitle}
                      </span>
                    </div>
                    <span
                      className={cn(
                        "text-[9px] uppercase tracking-wider font-bold",
                        confirmDialog.action === "complete"
                          ? "text-emerald-500"
                          : "text-amber-500",
                      )}
                    >
                      {confirmDialog.action === "complete"
                        ? "Completed"
                        : "Reset"}
                    </span>
                  </div>
                </div>
              )}
              {confirmDialog?.type === "workout" && (
                <div className="flex flex-col gap-2">
                  <p>Are you sure you want to update this exercise's status?</p>
                  <div className="my-1 p-2.5 bg-secondary/35 rounded-lg border border-border/40 flex items-center justify-between">
                    <span className="font-semibold text-foreground text-xs">
                      {confirmDialog.workoutTitle}
                    </span>
                    <span
                      className={cn(
                        "text-[9px] uppercase tracking-wider font-bold",
                        confirmDialog.action === "complete"
                          ? "text-emerald-500"
                          : "text-amber-500",
                      )}
                    >
                      {confirmDialog.action === "complete"
                        ? "Completed"
                        : "Reset"}
                    </span>
                  </div>
                </div>
              )}
              {confirmDialog?.type === "section" && (
                <div className="flex flex-col gap-2">
                  <p>Are you sure you want to update this section's status?</p>
                  <div className="my-1 p-2.5 bg-secondary/35 rounded-lg border border-border/40 flex items-center justify-between">
                    <span className="font-semibold text-foreground text-xs">
                      {confirmDialog.sectionName}
                    </span>
                    <span
                      className={cn(
                        "text-[9px] uppercase tracking-wider font-bold",
                        confirmDialog.action === "complete"
                          ? "text-emerald-500"
                          : "text-amber-500",
                      )}
                    >
                      {confirmDialog.action === "complete"
                        ? "Completed"
                        : "Reset"}
                    </span>
                  </div>
                </div>
              )}
              {confirmDialog?.type === "session-bulk" && (
                <div className="flex flex-col gap-2">
                  <div className="whitespace-pre-line">
                    {confirmDialog.description}
                  </div>
                </div>
              )}
            </div>
          </DialogHeader>
          <DialogFooter className="flex items-center flex-row justify-end gap-2 shrink-0">
            <button
              disabled={confirmSubmitting}
              onClick={() => setConfirmDialog(null)}
              className="px-4 py-1.5 text-xs font-heading font-mono font-black uppercase tracking-wider bg-secondary hover:bg-secondary/80 text-foreground rounded-lg transition-colors border-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              disabled={confirmSubmitting}
              onClick={async () => {
                setConfirmSubmitting(true);
                try {
                  await confirmDialog?.onConfirm?.();
                  setConfirmDialog(null);
                } catch (err) {
                  console.error(err);
                } finally {
                  setConfirmSubmitting(false);
                }
              }}
              className={cn(
                "px-4 py-1.5 text-xs font-heading font-black font-mono uppercase tracking-wider rounded-lg transition-colors border-0 flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed min-w-[70px]",
                confirmDialog?.action === "complete"
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : confirmDialog?.action === "reset"
                    ? "bg-amber-600 hover:bg-amber-700 text-white"
                    : "bg-primary hover:bg-primary/90 text-primary-foreground",
              )}
            >
              {confirmSubmitting && (
                <Loader2
                  className={cn(
                    "h-3 w-3 animate-spin",
                    confirmDialog?.action === "complete" ||
                      confirmDialog?.action === "reset"
                      ? "text-white"
                      : "text-primary-foreground",
                  )}
                />
              )}
              {confirmDialog?.action === "complete"
                ? "Mark as Complete"
                : confirmDialog?.action === "reset"
                  ? "Mark as Uncomplete"
                  : "Confirm"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Session Delete Confirmation Dialog */}
      <Dialog
        open={sessionDeleteConfirm}
        onOpenChange={setSessionDeleteConfirm}
      >
        <DialogContent className="bg-card border-border max-w-sm w-[90%] rounded-xl p-5 z-[99]">
          <DialogHeader>
            <DialogTitle className="font-heading text-sm font-black uppercase tracking-wide text-foreground flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-destructive" />
              Delete Session
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mt-2 leading-relaxed">
              Are you sure you want to delete the workout session for{" "}
              <strong>
                {session?.date
                  ? (() => {
                      const parts = session.date.split("-");
                      if (parts.length === 3) {
                        return new Date(
                          Number(parts[0]),
                          Number(parts[1]) - 1,
                          Number(parts[2]),
                        ).toLocaleDateString("en-IN", {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                        });
                      }
                      return session.date;
                    })()
                  : "this day"}
              </strong>
              ? All logged exercises, sets, and progress for this day will be
              permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 justify-end mt-4 flex-row">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSessionDeleteConfirm(false)}
              className="text-xs font-heading uppercase tracking-wide"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={deleting}
              onClick={async () => {
                setDeleting(true);
                try {
                  await onDeleteSession?.();
                  setSessionDeleteConfirm(false);
                } finally {
                  setDeleting(false);
                }
              }}
              className="text-xs font-heading uppercase tracking-wide bg-destructive text-destructive-foreground hover:bg-destructive/90 min-w-[70px] flex items-center justify-center gap-1.5"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Section Delete Confirmation Dialog */}
      <Dialog
        open={sectionDeleteConfirm.open}
        onOpenChange={(o) =>
          setSectionDeleteConfirm((prev) => ({ ...prev, open: o }))
        }
      >
        <DialogContent className="bg-card border-border max-w-sm w-[90%] rounded-xl p-5 z-[99]">
          <DialogHeader>
            <DialogTitle className="font-heading text-sm font-black uppercase tracking-wide text-foreground flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-destructive" />
              Delete Section
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mt-2 leading-relaxed">
              Are you sure you want to delete the section{" "}
              <strong>"{sectionDeleteConfirm.name}"</strong>? All exercises
              inside this section will be permanently removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 justify-end mt-4 flex-row">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setSectionDeleteConfirm({ open: false, si: null, name: "" })
              }
              className="text-xs font-heading uppercase tracking-wide"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={deleting}
              onClick={async () => {
                setDeleting(true);
                try {
                  await handleRemoveSection(sectionDeleteConfirm.si);
                  setSectionDeleteConfirm({ open: false, si: null, name: "" });
                } finally {
                  setDeleting(false);
                }
              }}
              className="text-xs font-heading uppercase tracking-wide bg-destructive text-destructive-foreground hover:bg-destructive/90 min-w-[70px] flex items-center justify-center gap-1.5"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
